import React, { useState } from 'react';
import { FormattedMessage, useIntl, type IntlShape } from 'react-intl';
import type { AskUserAnswer, AskUserQuestion } from '@hooks/useChat';
import ClarificationImage, { type NormBox } from './ClarificationImage';
import {
  Card,
  CardIcon,
  Head,
  Eyebrow,
  Progress,
  StepMeta,
  Bar,
  BarFill,
  Question,
  Options,
  OptionRow,
  OptionBadge,
  OptionLabel,
  OptionInput,
  NoteInput,
  Foot,
  Spacer,
  TextButton,
  FilledButton,
} from './ClarificationCard.styled';

const DECIDE_VALUE = '__decide_for_me__';
const FREE_TEXT_VALUE = '__free_text__';

const letter = (i: number): string => String.fromCodePoint(65 + i);

interface Option {
  value: string;
  label: string;
  icon?: string;
}

interface DrawnRegion {
  region: NormBox;
  crop: Blob | null;
}

export interface ClarificationRestore {
  answers: AskUserAnswer[];
  crops: File[];
}

interface SeededState {
  answers: Record<number, string>;
  otherText: Record<number, string>;
  regions: Record<number, DrawnRegion>;
}

/**
 * Rehydrate the wizard's in-progress state after a failed send remounts the card
 * (crop upload / pre-dispatch failure), so the user keeps their answers and the
 * box they drew instead of starting over. Crops are matched to questions by the
 * deterministic region-Q{n}.png name the submit path assigns.
 */
const seedFromRestore = (
  questions: AskUserQuestion[],
  restore?: ClarificationRestore,
): SeededState => {
  const seeded: SeededState = { answers: {}, otherText: {}, regions: {} };
  if (!restore) return seeded;
  const cropByName = new Map(restore.crops.map((f) => [f.name, f]));
  questions.forEach((_q, i) => {
    const a = restore.answers[i];
    if (!a) return;
    if (a.answerType === 'decide_for_me') seeded.answers[i] = DECIDE_VALUE;
    else if (a.answerType === 'free_text') {
      seeded.answers[i] = FREE_TEXT_VALUE;
      seeded.otherText[i] = a.answer;
    } else if (a.answerType === 'region') {
      if (a.answer) seeded.otherText[i] = a.answer;
      if (a.region) seeded.regions[i] = { region: a.region, crop: cropByName.get(`region-Q${i + 1}.png`) ?? null };
    } else if (a.answer) seeded.answers[i] = a.answer;
  });
  return seeded;
};

const buildOptions = (question: AskUserQuestion, intl: IntlShape): Option[] => {
  const opts: Option[] = question.answers.map((a) => ({ value: a, label: a }));
  if (question.allowDecideForMe !== false) {
    opts.push({
      value: DECIDE_VALUE,
      label: intl.formatMessage({ id: 'chat.clarify.decideForMe', defaultMessage: 'Decide for me' }),
      icon: 'auto_awesome',
    });
  }
  if (question.allowFreeText !== false) {
    opts.push({
      value: FREE_TEXT_VALUE,
      label: intl.formatMessage({ id: 'chat.clarify.somethingElse', defaultMessage: 'Something else…' }),
      icon: 'edit',
    });
  }
  return opts;
};

const toOptionAnswer = (
  question: AskUserQuestion,
  value: string | undefined,
  otherText: string,
): AskUserAnswer => {
  if (value === DECIDE_VALUE) {
    return { question: question.question, options: question.answers, answer: '', answerType: 'decide_for_me' };
  }
  if (value === FREE_TEXT_VALUE) {
    return { question: question.question, options: question.answers, answer: otherText.trim(), answerType: 'free_text' };
  }
  return { question: question.question, options: question.answers, answer: value ?? '', answerType: 'option' };
};

/** Clarifying-questions wizard docked above the composer (AIB-6745/AIB-7965). */
const ClarificationCard: React.FC<{
  questions: AskUserQuestion[];
  restore?: ClarificationRestore;
  /** An answer is already on its way, so this one must not be sent again. */
  disabled?: boolean;
  onSubmit: (answers: AskUserAnswer[], crops: File[]) => void;
}> = ({ questions, restore, disabled = false, onSubmit }) => {
  const intl = useIntl();
  const [seed] = useState(() => seedFromRestore(questions, restore));
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<Record<number, string>>(seed.answers);
  const [otherText, setOtherText] = useState<Record<number, string>>(seed.otherText);
  const [regions, setRegions] = useState<Record<number, DrawnRegion>>(seed.regions);

  const total = questions.length;
  const current = questions[step];
  if (!current) return null;

  const isAnnotate = !!current.expectsRegion && !!current.imageArtifactId;
  const value = answers[step];
  const isLast = step === total - 1;
  const pct = ((step + 1) / total) * 100;
  const freeTextEmpty = value === FREE_TEXT_VALUE && !(otherText[step] ?? '').trim();
  const canAdvance =
    !disabled && (isAnnotate ? !!regions[step] : !!value && !freeTextEmpty);

  const buildAnswer = (q: AskUserQuestion, i: number): AskUserAnswer => {
    if (q.expectsRegion && q.imageArtifactId) {
      return {
        question: q.question,
        options: [],
        answer: (otherText[i] ?? '').trim(),
        answerType: 'region',
        region: regions[i]?.region,
      };
    }
    return toOptionAnswer(q, answers[i], otherText[i] ?? '');
  };

  const collectCrops = (): File[] =>
    questions.flatMap((q, i) => {
      const drawn = regions[i];
      if (!q.expectsRegion || !drawn?.crop) return [];
      return [new File([drawn.crop], `region-Q${i + 1}.png`, { type: 'image/png' })];
    });

  const advance = () => {
    if (disabled) return;
    if (!isLast) {
      setStep((s) => s + 1);
      return;
    }
    onSubmit(questions.map(buildAnswer), collectCrops());
  };

  const onRegionDraw = (region: NormBox | undefined, crop: Blob | null) => {
    setRegions((prev) => {
      const next = { ...prev };
      if (region) next[step] = { region, crop };
      else delete next[step];
      return next;
    });
  };

  return (
    <Card>
      <Head>
        <Eyebrow>
          <CardIcon $size={15}>help</CardIcon>
          <FormattedMessage id="chat.clarify.eyebrow" defaultMessage="Clarifying" />
        </Eyebrow>
        <Progress>
          <StepMeta>
            <FormattedMessage
              id="chat.clarify.step"
              defaultMessage="{current} of {total}"
              values={{ current: step + 1, total }}
            />
          </StepMeta>
          <Bar>
            <BarFill $pct={pct} />
          </Bar>
        </Progress>
      </Head>

      {current.imageArtifactId && (
        <ClarificationImage
          key={`${current.id}-${isAnnotate ? 'draw' : 'view'}`}
          artifactId={current.imageArtifactId}
          region={current.region}
          regionLabel={current.regionLabel}
          annotate={isAnnotate}
          value={regions[step]?.region}
          onRegionDraw={isAnnotate ? onRegionDraw : undefined}
        />
      )}

      <Question>{current.question}</Question>

      {isAnnotate ? (
        <Options>
          <NoteInput
            placeholder={intl.formatMessage({
              id: 'chat.clarify.noteOptional',
              defaultMessage: 'Add a note (optional)…',
            })}
            value={otherText[step] ?? ''}
            onChange={(e) => setOtherText((t) => ({ ...t, [step]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && canAdvance) {
                e.preventDefault();
                advance();
              }
            }}
          />
        </Options>
      ) : (
        <Options>
          {buildOptions(current, intl).map((o, i) => {
            const selected = value === o.value;
            const showInput = o.value === FREE_TEXT_VALUE && selected;
            return (
              <OptionRow
                key={o.value}
                $selected={selected}
                $extra={!!o.icon}
                onClick={() => setAnswers((a) => ({ ...a, [step]: o.value }))}
              >
                <OptionBadge $selected={selected} $icon={!!o.icon}>
                  {o.icon ? <CardIcon $size={17}>{o.icon}</CardIcon> : letter(i)}
                </OptionBadge>
                {showInput ? (
                  <OptionInput
                    autoFocus
                    placeholder={intl.formatMessage({
                      id: 'chat.clarify.freeText',
                      defaultMessage: 'Type your answer…',
                    })}
                    value={otherText[step] ?? ''}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => setOtherText((t) => ({ ...t, [step]: e.target.value }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if ((otherText[step] ?? '').trim()) advance();
                      }
                    }}
                  />
                ) : (
                  <OptionLabel>{o.label}</OptionLabel>
                )}
              </OptionRow>
            );
          })}
        </Options>
      )}

      <Foot>
        {step > 0 && (
          <TextButton type="button" onClick={() => setStep((s) => Math.max(0, s - 1))}>
            <CardIcon $size={17}>arrow_back</CardIcon>
            <FormattedMessage id="chat.clarify.back" defaultMessage="Back" />
          </TextButton>
        )}
        <Spacer />
        <FilledButton type="button" disabled={!canAdvance} onClick={advance}>
          {isLast ? (
            <FormattedMessage id="chat.clarify.submit" defaultMessage="Submit" />
          ) : (
            <FormattedMessage id="chat.clarify.next" defaultMessage="Next" />
          )}
          <CardIcon $size={17}>arrow_forward</CardIcon>
        </FilledButton>
      </Foot>
    </Card>
  );
};

export default ClarificationCard;
